import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from 'react-i18next';

import DropdownMC from '../../components/DropdownMC';
import InputComponent from '../../components/InputMC';
import RightModalLayout from '../../layouts/Modal/RightModalLayout';
import FormProviderMC from '../../components/FormProviderMC';
import ButtonComponent from '../../components/ButtonMC';

import { SIDEBAR_ACTIONS } from '../../../constants/actionsConstants';
import { itemDiscount } from '../../../util/orderUtils';

const typeOfDiscounts = [
  { id: 'quantity', value: 'quantity', label: 'Cantidad' },
  { id: 'percentage', value: 'percentage', label: 'Porcentaje' }
];

const schema = z
  .object({
    discount_type: z
      .string()
      .min(1, 'Requerido'),
    discount_value: z
      .string()
      .min(1, "Requerido")
      .regex(/^\d+(\.\d{1,2})?$/, "Solo números"),
    discount_reason: z
      .string()
      .max(100, "Máximo 100 caracteres")
      .optional()
  })
  .refine(
    (data) => {
      if (data?.discount_type === 'percentage') {
        return Number(data.discount_value) <= 100;
      }
      return true;
    },
    {
      message: "Máximo 100",
      path: ["discount_value"]
    }
  );

const ManageProductDiscountModal = ({
  lineItem,
  selectedProducts,
  handleSetSelectedProducts,
  updateOrder
}) => {
  const { t } = useTranslation('Orders');
  const dispatch = useDispatch();
  const { retailer_info = {} } = useSelector((reduxState) => reduxState.retailerUsersReducer);

  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      discount_type: '',
      discount_value: '',
      discount_reason: ''
    }
  });

  const {
    watch,
    setValue,
    getValues
  } = methods;

  watch('discount_type');
  watch('discount_reason');

  useEffect(() => {
    const { discount_type, discount_value, discount_reason } = lineItem;
    fillFormData({ discount_type, discount_value, discount_reason });
  }, [lineItem]);

  const fillFormData = useCallback((data) => {
    setValue('discount_type', data.discount_type ?? '');
    setValue('discount_value', data.discount_value ?? '');
    setValue('discount_reason', data.discount_reason ?? '');
  }, [setValue]);

  const handleRemoveDiscount = () => {
    onSubmit({ discount_type: '', discount_value: '', reason: '' });
  };

  const onSubmit = (data) => {
    if (lineItem.forOrder) {
      updateOrder({
        discount_type: data.discount_type,
        discount_value: data.discount_value,
        discount_reason: data.discount_reason,
        total_discount: itemDiscount(lineItem.subtotal, data.discount_type, data.discount_value)
      });

      closeAction();
      return;
    }

    const newItems = selectedProducts.map((product) => (product.lineIdentifier === lineItem.lineIdentifier
      ? {
        ...product,
        discount_type: data.discount_type,
        discount_value: data.discount_value,
        discount_reason: data.discount_reason,
        total_discount: 0
      }
      : product));

    handleSetSelectedProducts(newItems);

    closeAction();
  };

  const closeAction = () => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: {
        componentKey: 'ManageProductDiscountModal'
      }
    });
  };

  return (
    <RightModalLayout
      closeAction={closeAction}
      title="Agregar descuento"
    >
      <div className="tw-wrapper">
        <FormProviderMC
          methods={methods}
        >
          <div className="tw-flex tw-flex-col tw-pb-6 tw-gap-4">
            <div className="tw-flex tw-flex-col tw-gap-2">
              <span className="tw-font-semibold tw-text-gray-950 tw-text-sm">
                {t('manageDiscount.title')}
              </span>

              <div className="tw-flex tw-gap-2">
                <DropdownMC
                  useFloatingLabel
                  id="discount_type"
                  name="discount_type"
                  label={t('manageDiscount.typeLabel')}
                  options={typeOfDiscounts}
                  defaultValue={methods.getValues('discount_type')}
                />
                <div className="tw-w-full tw-relative">
                  <InputComponent
                    useFloatingLabel
                    id="discount_value"
                    name="discount_value"
                    label={t('manageDiscount.valueLabel')}
                    options={typeOfDiscounts}
                    defaultValue={methods.getValues('discount_value')}
                  />
                  <span className="tw-absolute tw-text-gray-400 tw-text-xs tw-right-2.5 tw-top-6">
                    {getValues("discount_type") === 'percentage' ? '%' : `${retailer_info.currency_code || 'USD'}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="tw-flex tw-flex-col tw-gap-2">
              <div className="tw-flex tw-flex-col">
                <span className="tw-font-semibold tw-text-gray-950 tw-text-sm">
                  {t('manageDiscount.reasonLabel')}
                </span>
                <span className="tw-text-gray-700 tw-text-xs">{t('manageDiscount.reasonDescription')}</span>
              </div>

              <div className="tw-w-full tw-flex-col tw-relative">
                <InputComponent
                  id="discount_reason"
                  name="discount_reason"
                  label={t('manageDiscount.reazon')}
                  placeholder={t('manageDiscount.reasonPlaceholder')}
                  className="tw-resize-none tw-w-full !tw-placeholder-gray-400"
                  textarea
                  useFloatingLabel
                  onInput={(e) => {
                    if (e.target.value.length > 100) {
                      e.target.value = e.target.value.slice(0, 100);
                    }
                  }}
                />

                <span className="tw-absolute tw-text-gray-400 tw-text-xs tw-right-[10px] tw-bottom-[5px]">
                  {`${getValues("discount_reason")?.length || 0} / 100`}
                </span>
              </div>
            </div>

            {lineItem.discount_type && (
              <div className="tw-flex tw-flex-col tw-gap-2">
                <span className="tw-font-semibold tw-text-gray-950 tw-text-sm">
                  {t('manageDiscount.actions')}
                </span>

                <div className="tw-flex">
                  <ButtonComponent
                    text="Eliminar descuento"
                    onClick={handleRemoveDiscount}
                    variant="SECONDARY"
                    outlined
                  />
                </div>
              </div>
            )}
          </div>

          <div className="tw-flex tw-flex-col tw-px-12 tw-py-4 tw-gap-2">
            <ButtonComponent
              text="Agregar descuento"
              onClick={methods.handleSubmit(onSubmit)}
              className="tw-font-medium tw-text-sm tw-w-full tw-px-8 tw-py-2"
            />
            <div className="tw-font-medium tw-text-blue-500 tw-text-sm tw-text-center tw-px-8 tw-py-2 tw-cursor-pointer" onClick={closeAction}>{t('manageDiscount.cancel')}</div>
          </div>
        </FormProviderMC>
      </div>
    </RightModalLayout>
  );
};

export default ManageProductDiscountModal;
